import { useEffect, useState } from 'react';

/** What the server says about itself: staging must never be mistaken for the live warehouse. */
export interface AppInfo {
  /** production · staging · demo · development */
  readonly environment: string;
  readonly version: string;
}

let pending: Promise<AppInfo> | null = null;

export function loadAppInfo(): Promise<AppInfo> {
  pending ??= fetch('/api/info')
    .then((r) => (r.ok ? (r.json() as Promise<AppInfo>) : { environment: 'production', version: '' }))
    .catch(() => ({ environment: 'production', version: '' }));
  return pending;
}

export function useAppInfo(): AppInfo | null {
  const [info, setInfo] = useState<AppInfo | null>(null);
  useEffect(() => {
    let alive = true;
    void loadAppInfo().then((i) => alive && setInfo(i));
    return () => {
      alive = false;
    };
  }, []);
  return info;
}

/**
 * Whether the server now runs another version than this page was loaded with (a deploy since): an
 * open page keeps its old code until it is reloaded. Asked every two minutes and whenever the tab
 * comes back into view; a tab nobody is looking at reloads by itself.
 */
export function useNewVersion(): boolean {
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    let alive = true;
    const check = async () => {
      const loaded = (await loadAppInfo()).version;
      try {
        const res = await fetch('/api/info', { cache: 'no-store' });
        const { version } = res.ok ? ((await res.json()) as AppInfo) : { version: '' };
        if (!alive || !loaded || loaded === 'dev' || !version || version === loaded) return;
        if (document.visibilityState === 'hidden') window.location.reload();
        else setNewer(true);
      } catch {
        // offline for a moment: asked again later
      }
    };
    const timer = setInterval(() => void check(), 120_000);
    const onVisibility = () => void check();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  return newer;
}

/** The ribbon text of a non-production server, or null. */
export function environmentLabel(environment: string | undefined): { readonly text: string; readonly short: string; readonly tone: 'bad' | 'warn' | 'info' } | null {
  switch (environment) {
    case 'staging':
      return { text: 'TEST ORTAMI · gerçek sipariş yok', short: 'TEST', tone: 'bad' };
    case 'demo':
      return { text: 'DEMO · örnek veriler', short: 'DEMO', tone: 'warn' };
    case 'development':
      return { text: 'GELİŞTİRME', short: 'DEV', tone: 'info' };
    default:
      return null;
  }
}
